import {
  CSEAssessmentSection,
  CustomerDetailSection,
  CustomerHealthSection,
  PartnerAssessmentSection,
  VidcastAssessmentSection,
  WorkloadAssessmentSection,
  EpsmPartnerAssessmentSection,
} from "components/summary/success-plan";
import { SuccessPlanState } from "components/summary/success-plan/state/successPlan.types";
import styles from "./SuccessPlan.module.scss";
import { FEATURE_KEY } from "constant";
import { AuthProvider } from "pages";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Breadcrumbs, IconButton, Link } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";

type SuccessPlanSectionGroupProps = {
  spState: SuccessPlanState;
};

const sections = [
  { id: "csmSection", label: "Overall Customer Health Risk" },
  { id: "workloadSection", label: "Workload Assessment" },
  { id: "customerDetailsSection", label: "Customer Details" },
  { id: "cseSection", label: "CSE Assessment" },
  { id: "partnerSection", label: "Partner Success Assessment" },
  { id: "epsmPartnerSection", label: "EPSM Partner Assessment" },
  { id: "vidcastSection", label: "Vidcast Assessment" },
];

function getScrollSection(features: FEATURE_KEY[]): string | null {
  if (features.includes(FEATURE_KEY.EDIT_EXPAND_PLAN_REVAMP)) {
    return "csmSection";
  }
  if (features.includes(FEATURE_KEY.UPDATE_CONCIERGE)) {
    return "cseSection";
  }
  if (features.includes(FEATURE_KEY.EDIT_PARTNER_SUCCESS)) {
    return "partnerSection";
  }
  if (features.includes(FEATURE_KEY.EDIT_EPSM_PARTNER_SUCCESS)) {
    return "epsmPartnerSection";
  }
  if (features.includes(FEATURE_KEY.EDIT_VIDCAST_SUCCESS)) {
    return "vidcastSection";
  }
  return null;
}

const SCROLL_STEP = 150;
const HOLD_SCROLL_SPEED = 3;

const useHoldScroll = (
  ref: React.RefObject<HTMLElement | null>,
  direction: "left" | "right",
  onScroll: () => void
) => {
  const rafId = useRef(0);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafId.current);
    rafId.current = 0;
  }, []);

  const start = useCallback(() => {
    const tick = () => {
      const el = ref.current;
      if (el) {
        el.scrollLeft += direction === "left" ? -HOLD_SCROLL_SPEED : HOLD_SCROLL_SPEED;
        onScroll();
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
  }, [ref, direction, onScroll]);

  useEffect(() => stop, [stop]);

  return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop };
};

const SuccessPlanSectionGroup = ({ spState }: SuccessPlanSectionGroupProps) => {
  const { features } = useContext(AuthProvider);
  const scrollSection = getScrollSection(features ?? []);
  const contentRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLElement>(null);
  const [activeSection, setActiveSection] = useState<string | null>(scrollSection);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = breadcrumbRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth;
    setIsOverflowing(overflowing);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = breadcrumbRef.current;
    if (!el) return;
    updateScrollState();
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScrollState]);

  const handleScrollLeft = () => {
    breadcrumbRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    breadcrumbRef.current?.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });
  };

  const holdLeftProps = useHoldScroll(breadcrumbRef, "left", updateScrollState);
  const holdRightProps = useHoldScroll(breadcrumbRef, "right", updateScrollState);

  const scrollToSection = (sectionId: string) => {
    const container = contentRef.current;
    const element = document.getElementById(sectionId);
    if (container && element) {
      const top =
        element.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      container.scrollTo({ top, behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    if (scrollSection) {
      setTimeout(() => {
        scrollToSection(scrollSection!);
        setActiveSection(scrollSection);
      }, 300);
    }
  }, []);

  return (
    <section className={styles["sp-section"]}>
      <div className={styles["sp-section-breadcrumbs-wrapper"]}>
        {isOverflowing && (
          <IconButton
            size="small"
            onClick={handleScrollLeft}
            disabled={!canScrollLeft}
            className={styles["sp-breadcrumb-arrow"]}
            {...holdLeftProps}
          >
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs
          maxItems={sections.length}
          separator="|"
          aria-label="breadcrumb"
          className={styles["sp-section-breadcrumbs"]}
          ref={breadcrumbRef}
          onScroll={updateScrollState}
        >
          {sections.map((section) => (
            <Link
              key={section.id}
              underline="hover"
              color={activeSection === section.id ? "primary" : "inherit"}
              onClick={() => scrollToSection(section.id)}
              href="#"
              sx={{ whiteSpace: "nowrap" }}
            >
              {section.label}
            </Link>
          ))}
        </Breadcrumbs>
        {isOverflowing && (
          <IconButton
            size="small"
            onClick={handleScrollRight}
            disabled={!canScrollRight}
            className={styles["sp-breadcrumb-arrow"]}
            {...holdRightProps}
          >
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        )}
      </div>
      <div
        className={styles["sp-section-content"]}
        ref={contentRef}
      >
        <div id="csmSection">
          <CustomerHealthSection spState={spState} />
        </div>
        <div id="workloadSection">
          <WorkloadAssessmentSection spState={spState} />
        </div>
        <div id="customerDetailsSection">
          <CustomerDetailSection spState={spState} />
        </div>
        <div id="cseSection">
          <CSEAssessmentSection spState={spState} />
        </div>
        <div id="partnerSection">
          <PartnerAssessmentSection spState={spState} />
        </div>
        <div id="epsmPartnerSection">
          <EpsmPartnerAssessmentSection spState={spState} />
        </div>
        <div id="vidcastSection">
          <VidcastAssessmentSection spState={spState} />
        </div>
      </div>
    </section>
  );
};
export default SuccessPlanSectionGroup;
